import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Tooltip,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  GetNextGoodsOutNo,
  createIssue,
  ApproveSolistapi,
  getDivisionUnits,
  updateIssue,
  getCategories,
  fetchSubCategories,
  fetchItemsByCategoryAndSubcategory,
  getSizes,
} from "../../../Services/Services.jsx";
import AddItemForm from "../Inventory/ItemForm";
import { useNavigate, useLocation } from "react-router-dom";
import { useCallback } from "react";
import { debounce } from "lodash";
import { CircularProgress } from "@mui/material";

const IssueForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getSelectedDivision = () => {
    try {
      const stored = localStorage.getItem("selectedDivision");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("Failed to parse selectedDivision:", e);
      return null;
    }
  };
  const storedDivision = getSelectedDivision();

  const [unitOptions, setUnitOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectPage, setProjectPage] = useState(1);
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectSearch, setProjectSearch] = useState("");
  const [itemOptions, setItemOptions] = useState({});
  const [subcategories, setSubcategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [size, setSizes] = useState([]);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [openItemModal, setOpenItemModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [formErrors, setFormErrors] = useState({});
  const [rowErrors, setRowErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const issueData = location.state?.issue || null;
  const isEditMode = !!issueData?.id;

  const [formData, setFormData] = useState({
    go_no: "",
    slip_no: "",
    labour_name: "",
    division: storedDivision?.division_id || "",
    division_name: storedDivision?.division || "",
    remarks: "",
    project: "",
    created_by: localStorage.getItem("user_id"),
  });

  // Create empty row function
  function createEmptyRow() {
    return {
      category: null,
      sub_category: null,
      item_description: null,
      uom: "",
      stock_qty: "0",
      issue_qty: "0",
      size: null,
    };
  }

  const [rows, setRows] = useState([createEmptyRow()]);

  // Fetch dropdown data
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [division_units, catRes, sizeData] = await Promise.all([
          getDivisionUnits(),
          getCategories(),
          getSizes(),
        ]);

        setUnitOptions(division_units.units || []);
        setCategories(Array.isArray(catRes) ? catRes : catRes?.results || []);
        setSizes(Array.isArray(sizeData) ? sizeData : sizeData?.results || []);
        setIsDataLoaded(true);

        const divisionData = getSelectedDivision();
        if (divisionData?.division_id) {
          fetchProjects(1, {}, divisionData.division_id);
        }

        if (issueData) {
          const divisionData = getSelectedDivision();
        }

        if (issueData) {
          setFormData({
            go_no: issueData.go_no || "",
            slip_no: issueData.slip_no || "",
            labour_name: issueData.labour_name || "",
            division: divisionData?.division_id || issueData.division || "",
            division_name:
              divisionData?.division || issueData.division_name || "",
            remarks: issueData.remarks || "",
            project: issueData.project || "",
            created_by: issueData.created_by || localStorage.getItem("user_id"),
          });

          const processItems = async () => {
            const mappedItems = await Promise.all(
              (issueData.items || []).map(async (item, index) => {
                const foundCategory = item.category
                  ? categories.find(
                      (c) => String(c.id) === String(item.category),
                    )
                  : null;

                let subcatList = [];
                if (item.category) {
                  try {
                    subcatList = await fetchSubCategories(item.category);
                    setSubcategories((prev) => ({
                      ...prev,
                      [index]: subcatList,
                    }));
                  } catch (error) {
                    console.error("Error fetching subcategories:", error);
                  }
                }

                const foundSubCategory =
                  item.sub_category && subcatList.length > 0
                    ? subcatList.find(
                        (s) =>
                          String(s.subcat_id) === String(item.sub_category),
                      )
                    : null;

                let itemList = [];
                if (item.category && item.sub_category) {
                  try {
                    const itemData = await fetchItemsByCategoryAndSubcategory(
                      item.category,
                      item.sub_category,
                    );
                    itemList = itemData.results || [];
                    setItemOptions((prev) => ({
                      ...prev,
                      [index]: itemList.map((item) => ({
                        id: item.id,
                        name: item.name,
                        unit_name: item.uom || item.unit_name || "",
                        current_stock: item.current_stock,
                        size_id: item.size || null,
                        size_name: item.size_name || "",
                      })),
                    }));
                  } catch (error) {
                    console.error("Error fetching items:", error);
                  }
                }

                const foundItem =
                  item.item && itemList.length > 0
                    ? itemList.find((i) => String(i.id) === String(item.item))
                    : null;

                const foundSize =
                  item.size && size.length > 0
                    ? size.find((s) => String(s.id) === String(item.size))
                    : null;

                return {
                  category: foundCategory || {
                    id: item.category || item.category?.id,
                    name: item.category?.name || item.category_name || "",
                  },
                  sub_category: foundSubCategory || {
                    subcat_id:
                      item.sub_category || item.sub_category?.subcat_id,
                    name:
                      item.sub_category?.name || item.sub_category_name || "",
                  },
                  item_description: foundItem
                    ? {
                        id: foundItem.id,
                        name: foundItem.name,
                        unit_name: foundItem.unit_name || item.uom,
                        current_stock:
                          foundItem.current_stock || item.stock_qty,
                        size_id: foundItem.size_id,
                        size_name: foundItem.size_name,
                      }
                    : {
                        id: item.item || item.item?.id,
                        name: item.item?.name || item.item_description || "",
                        unit_name: item.uom,
                        current_stock: item.stock_qty,
                        size_id: item.size,
                        size_name: "",
                      },
                  uom: item.uom || "",
                  stock_qty: item.stock_qty || "0",
                  issue_qty: item.issue_qty || "0",
                  size: foundSize || null,
                };
              }),
            );

            setRows(mappedItems.length > 0 ? mappedItems : [createEmptyRow()]);
          };

          processItems();
        } else {
          fetchNextGoodsOutNo();
        }
      } catch (err) {
        toast.error("Failed to load dropdowns");
      }
    };

    fetchDropdowns();
  }, []);

  // Fetch next goods out number for new issue
  const fetchNextGoodsOutNo = async () => {
    try {
      const data = await GetNextGoodsOutNo();
      if (data && data.go_no) {
        setFormData((prev) => ({
          ...prev,
          go_no: data.go_no,
        }));
      }
    } catch (err) {
      toast.error("Failed to fetch Goods Out No");
    }
  };

  // Fetch projects based on division
  const fetchProjects = async (page = 1, filters = {}, divisionId = "") => {
    try {
      setProjectLoading(true);
      const divisionData = getSelectedDivision();
      const divisionId = divisionData?.division_id;

      const finalFilters = divisionId
        ? { ...filters, division: divisionId }
        : filters;

      const response = await ApproveSolistapi(page, finalFilters);
      const results = Array.isArray(response.results)
        ? response.results
        : response || [];

      if (page === 1) {
        setProjectOptions(results);
      } else {
        setProjectOptions((prev) => [...prev, ...results]);
      }

      setProjectPage(page);
    } catch (err) {
      toast.error("Failed to load project list");
    } finally {
      setProjectLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchProjects(1, { project_name: projectSearch });
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [projectSearch]);

  // Handle search input changes with debounce
  const handleSearchInputChange = useCallback(
    debounce(async (index, type, row, newValue) => {
      if (!newValue || newValue.trim() === "") {
        if (type === "category") {
          const catRes = await getCategories();
          setCategories(Array.isArray(catRes) ? catRes : catRes?.results || []);
        }
        if (type === "sub_category" && row.category) {
          const subcats = await fetchSubCategories(row.category.id);
          setSubcategories((prev) => {
            const updated = [...prev];
            updated[index] = subcats;
            return updated;
          });
        }
        if (type === "item" && row.category && row.sub_category) {
          const itemData = await fetchItemsByCategoryAndSubcategory(
            row.category.id,
            row.sub_category.subcat_id,
            1,
            "",
          );
          setItemOptions((prev) => ({
            ...prev,
            [index]: itemData.results || [],
          }));
        }
        if (type === "size") {
          const sizeData = await getSizes();
          setSizes(
            Array.isArray(sizeData) ? sizeData : sizeData?.results || [],
          );
        }
        return;
      }

      if (type === "category") {
        const catRes = await getCategories(1, newValue);
        setCategories(Array.isArray(catRes) ? catRes : catRes?.results || []);
      }

      if (type === "sub_category" && row.category) {
        const subcats = await fetchSubCategories(row.category.id, 1, newValue);
        setSubcategories((prev) => {
          const updated = [...prev];
          updated[index] = subcats;
          return updated;
        });
      }

      if (type === "item" && row.category && row.sub_category) {
        const itemData = await fetchItemsByCategoryAndSubcategory(
          row.category.id,
          row.sub_category.subcat_id,
          1,
          newValue,
        );
        setItemOptions((prev) => ({
          ...prev,
          [index]: itemData.results || [],
        }));
      }

      if (type === "size") {
        const sizeData = await getSizes(newValue || "");
        const list = Array.isArray(sizeData.results)
          ? sizeData.results
          : sizeData;
        setSizes(list);
      }
    }, 500),
    [],
  );

  // Handle input changes in rows
  const handleInputChange = async (index, key, value) => {
    const updatedRows = [...rows];
    let row = { ...updatedRows[index] };

    switch (key) {
      

      case "category":
        row = {
          ...row,
          category: value,
          item_description: null,
          size: null,
        };
        if (value) {
          const itemData = await fetchItemsByCategoryAndSubcategory(
            value.id,
            0,
            page,
            searchTerm,
          );
          const mappedItems = (itemData.results || []).map((item) => ({
            id: item.id,
            name: item.name,
            unit_name: item.uom || item.unit_name,
            current_stock: item.current_stock || 0,
            size_id: item.size || null,
            size_name: item.size_name || "",
          }));

          setItemOptions((prev) => ({
            ...prev,
            [index]: mappedItems,
          }));
        }
        break;

      case "item_description":
        const newRow = { ...row };

        // Set item details
        newRow.item_description = value;

        // Set UOM from item if available
        if (value?.unit_name && !newRow.uom) {
          const unit = unitOptions.find(
            (u) => u.name === value.unit_name || u.symbol === value.unit_name,
          );
          if (unit) {
            newRow.uom = unit.symbol || unit.name;
          }
        }

        // Set stock quantity
        newRow.stock_qty = value?.current_stock || "0";

        // Set size if item has size data
        if (value?.size_id && !newRow.size) {
          const foundSize = size.find(
            (s) =>
              String(s.id) === String(value.size_id) ||
              s.name === value.size_name ||
              s.size === value.size_name,
          );
          if (foundSize) {
            newRow.size = foundSize;
          }
        }

        // Reset issue quantity to 0 when item changes
        newRow.issue_qty = "0";

        // Show warning if stock is 0
        if (parseFloat(newRow.stock_qty) === 0) {
          toast.warning(`Stock is unavailable for "${value?.name}"`);
        }

        row = newRow;
        break;

      case "issue_qty":
        const issueQty = parseFloat(value) || 0;
        const stockQty = parseFloat(row.stock_qty) || 0;

        if (issueQty > stockQty) {
          toast.error(
            `Issue Qty cannot be greater than Stock Qty in row ${index + 1}`,
          );
          return;
        }
        row.issue_qty = value;
        break;

      case "size":
        row = { ...row, size: value };
        break;

      case "uom":
        row = { ...row, uom: value };
        break;

      default:
        row[key] = value;
        break;
    }

    updatedRows[index] = row;
    setRows(updatedRows);
  };

  // Handle form field changes
  const handleFormChange = async (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: "" }));
  };
  // Add new row
  const handleAddRow = () => {
    setRows([...rows, createEmptyRow()]);
  };

  // Delete row
  const handleDeleteRow = (index) => {
    const updatedRows = [...rows];
    updatedRows.splice(index, 1);
    setRows(updatedRows);
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    const rowErrs = [];

    if (!formData.slip_no) errors.slip_no = "Slip No. is required";
    if (!formData.labour_name) errors.labour_name = "Department is required";
    if (!formData.project) errors.project = "Project is required";

    rows.forEach((row, i) => {
      const err = {};
      if (!row.category) err.category = "Required";
      // if (!row.sub_category) err.sub_category = "Required";
      if (!row.item_description) err.item_description = "Required";
      if (!row.uom) err.uom = "Required";
      if (!row.issue_qty || parseFloat(row.issue_qty) <= 0)
        err.issue_qty = "Must be greater than 0";
      rowErrs[i] = err;
    });

    setFormErrors(errors);
    setRowErrors(rowErrs);

    return (
      Object.keys(errors).length === 0 &&
      rowErrs.every((e) => Object.keys(e).length === 0)
    );
  };

  // Handle form submission
  const handleSubmit = async () => {
    const divisionData = getSelectedDivision();
    // if (!divisionData?.division_id) {
    //   toast.error(
    //     "No division selected. Please select a division from the header.",
    //   );
    //   return;
    // }

    if (!validateForm()) {
      toast.error("Please fill all required fields correctly");
      return;
    }

    setLoading(true);

    const itemsPayload = rows
      .map((row) => {
        const existingItem =
          isEditMode &&
          issueData?.items?.find(
            (item) => String(item.item) === String(row.item_description?.id),
          );

        return {
          item: row.item_description?.id || existingItem?.item,
          category: row.category?.id || existingItem?.category || null,
          sub_category:
            row.sub_category?.subcat_id || existingItem?.sub_category || null,
          item_description:
            row.item_description?.name || existingItem?.item_description || "",
          uom:
            unitOptions.find((u) => u.symbol === row.uom)?.id ||
            row.uom ||
            null,
          stock_qty: parseFloat(row.stock_qty || 0),
          issue_qty: parseFloat(row.issue_qty || 0),
          size: row.size?.id || existingItem?.size || null,
          remarks: row.remarks || "",
        };
      })
      .filter((item) => item.item); // Filter out items without ID

    const payload = {
      go_no: formData.go_no,
      slip_no: formData.slip_no,
      created_by: formData.created_by,
      // division: divisionData.division_id,
      remarks: formData.remarks,
      project: formData.project,
      labour_name: formData.labour_name,
      items: itemsPayload,
    };

    try {
      let res;

      if (isEditMode) {
        res = await updateIssue(issueData.id, payload);
        toast.success("Issue updated successfully!");
        navigate("/dashboard/issue-list");
      } else {
        res = await createIssue(payload);
        toast.success("Issue created successfully!");

        // Reset form
        setFormData({
          go_no: "",
          slip_no: "",
          labour_name: "",
          // division: storedDivision?.division_id || "",
          // division_name: storedDivision?.division || "",
          project: "",
          remarks: "",
          created_by: localStorage.getItem("user_id"),
        });

        setRows([createEmptyRow()]);

        // Fetch new goods out number
        fetchNextGoodsOutNo();
      }
    } catch (err) {
      const errData = err?.response?.data;
      if (errData?.item) {
        toast.error(`Item Error: ${errData.item[0]}`);
      } else if (errData?.message) {
        toast.error(errData.message);
      } else {
        toast.error("Submission failed. Please check the form.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleCloseItemModal = async () => {
    setOpenItemModal(false);
  };

  return (
    <Box p={2}>
      {/* ✅ Buttons - Mobile Stack / Desktop Horizontal */}
      {isMobile ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mt: -3,
            pt: 0,
            mb: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{
              borderRadius: 0,
              height: 35,
              fontSize: "0.7rem",
              textTransform: "none",
            }}
          >
            ← Back
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              borderRadius: 0,
              height: 35,
              fontSize: "0.7rem",
              textTransform: "none",
            }}
          >
            {loading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              "Submit"
            )}
          </Button>
        </Box>
      ) : (
        <Grid
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 3,
            alignItems: "flex-end",
            width: "100%",
            mt: -5,
            pt: 0,
            mb: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{
              borderRadius: 0,
              height: 35,
              minWidth: 120,
              fontSize: "0.7rem",
              px: 2,
              textTransform: "none",
            }}
          >
            ← Back
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              borderRadius: 0,
              height: 35,
              minWidth: 120,
              fontSize: "0.7rem",
              px: 2,
              textTransform: "none",
            }}
          >
            {loading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              "Submit"
            )}
          </Button>
        </Grid>
      )}

      <ToastContainer position="top-right" autoClose={3000} />

      <Box
        border="1px solid #ccc"
        borderRadius="8px"
        p={2}
        sx={{ backgroundColor: "#fff" }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Issue note No."
              name="go_no"
              value={formData.go_no || ""}
              disabled
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Req Slip No"
              required
              fullWidth
              value={formData.slip_no}
              onChange={(e) => handleFormChange("slip_no", e.target.value)}
              error={!!formErrors.slip_no}
              helperText={formErrors.slip_no}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Department"
              required
              fullWidth
              value={formData.labour_name}
              onChange={(e) => handleFormChange("labour_name", e.target.value)}
              error={!!formErrors.labour_name}
              helperText={formErrors.labour_name}
              size="small"
            />
          </Grid>
          {/* <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Division"
              size="small"
              value={storedDivision?.division || "No Division Selected"}
              disabled
              InputProps={{
                sx: {
                  backgroundColor: "#f5f5f5",
                  height: 40,
                },
              }}
              helperText="Division is set globally"
              error={!storedDivision?.division_id}
            />
          </Grid> */}
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={projectOptions}
              getOptionLabel={(opt) => opt.project_name || ""}
              value={
                Array.isArray(projectOptions)
                  ? projectOptions.find((opt) => opt.id === formData.project) ||
                    null
                  : null
              }
              loading={projectLoading}
              onChange={(e, newVal) => {
                handleFormChange("project", newVal?.id || "");
              }}
              onInputChange={(e, newInputValue) => {
                setProjectSearch(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  fullWidth
                  label="Project"
                  error={!!formErrors.project}
                  helperText={formErrors.project}
                  size="small"
                />
              )}
              sx={{ "& .MuiInputBase-root": { height: 40 } }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Remarks"
              required
              fullWidth
              value={formData.remarks}
              onChange={(e) => handleFormChange("remarks", e.target.value)}
              error={!!formErrors.remarks}
              helperText={formErrors.remarks}
              size="small"
            />
          </Grid>
        </Grid>

        {/* ✅ Table / Mobile Rows */}
        <Box mt={3}>
          {isMobile ? (
            // ✅ Mobile View - Vertical Stack
            <Box>
              {rows.map((row, index) => (
                <Card key={index} sx={{ mb: 2, border: "1px solid #ddd" }}>
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Typography variant="h6">Item #{index + 1}</Typography>
                      <IconButton
                        onClick={() => handleDeleteRow(index)}
                        color="error"
                        size="small"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {/* Category */}
                      <Autocomplete
                        options={categories}
                        getOptionLabel={(option) => option.name || ""}
                        value={row.category || null}
                        onChange={async (e, val, reason) => {
                          if (reason === "clear") {
                            handleInputChange(index, "category", null);
                            setSubcategories((prev) => {
                              const newSubcategories = [...prev];
                              newSubcategories[index] = [];
                              return newSubcategories;
                            });
                          }
                          handleInputChange(index, "category", val);
                        }}
                        onInputChange={(e, newValue, reason) => {
                          if (reason === "input") {
                            handleSearchInputChange(
                              index,
                              "category",
                              row,
                              newValue,
                            );
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Category"
                            size="small"
                            fullWidth
                            required
                            error={!!rowErrors[index]?.category}
                            helperText={rowErrors[index]?.category}
                          />
                        )}
                      />

                      {/* SubCategory */}
                      <Autocomplete
                        options={subcategories[index] || []}
                        getOptionLabel={(option) => option.name || ""}
                        value={row.sub_category || null}
                        onChange={async (e, val, reason) => {
                          if (reason === "clear") {
                            await handleSearchInputChange(
                              index,
                              "sub_category",
                              row,
                              "",
                            );
                            handleInputChange(index, "sub_category", null);
                          }
                          handleInputChange(index, "sub_category", val);
                        }}
                        onInputChange={(e, newValue, reason) => {
                          if (reason === "input") {
                            handleSearchInputChange(
                              index,
                              "sub_category",
                              row,
                              newValue,
                            );
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Subcategory"
                            size="small"
                            fullWidth
                            required
                            error={!!rowErrors[index]?.sub_category}
                            helperText={rowErrors[index]?.sub_category}
                          />
                        )}
                      />

                      {/* Item */}
                      <Autocomplete
                        options={itemOptions[index] || []}
                        getOptionLabel={(option) => option.name || ""}
                        isOptionEqualToValue={(option, value) =>
                          option?.id === value?.id
                        }
                        value={row.item_description || null}
                        onChange={async (e, newVal, reason) => {
                          if (reason === "clear") {
                            await handleSearchInputChange(
                              index,
                              "item",
                              row,
                              "",
                            );
                            handleInputChange(index, "item_description", null);
                          }
                          handleInputChange(index, "item_description", newVal);
                        }}
                        onInputChange={(e, newValue, reason) => {
                          if (reason === "input") {
                            handleSearchInputChange(
                              index,
                              "item",
                              row,
                              newValue,
                            );
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required
                            label="Item"
                            variant="outlined"
                            size="small"
                            fullWidth
                            error={!!rowErrors[index]?.item_description}
                            helperText={rowErrors[index]?.item_description}
                          />
                        )}
                      />

                      {/* Size */}
                      <Autocomplete
                        options={size}
                        getOptionLabel={(opt) => opt?.size || opt?.name || ""}
                        isOptionEqualToValue={(option, value) =>
                          option?.id === value?.id
                        }
                        value={row.size || null}
                        onChange={(e, val, reason) => {
                          if (reason === "clear" || !val) {
                            handleInputChange(index, "size", null);
                            return;
                          }
                          handleInputChange(index, "size", val);
                        }}
                        onInputChange={(e, newValue, reason) => {
                          if (reason === "input") {
                            handleSearchInputChange(
                              index,
                              "size",
                              row,
                              newValue,
                            );
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Size"
                            size="small"
                            fullWidth
                          />
                        )}
                      />

                      {/* UOM */}
                      <Autocomplete
                        options={unitOptions}
                        getOptionLabel={(option) =>
                          option.symbol || option.name || ""
                        }
                        isOptionEqualToValue={(option, value) =>
                          option?.id === value?.id
                        }
                        value={
                          unitOptions.find((opt) => opt.symbol === row.uom) ||
                          (row.uom ? { symbol: row.uom, name: row.uom } : null)
                        }
                        onChange={(e, newVal) => {
                          handleInputChange(
                            index,
                            "uom",
                            newVal?.symbol || newVal?.name || "",
                          );
                          const updated = [...rowErrors];
                          updated[index] = { ...updated[index], uom: "" };
                          setRowErrors(updated);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required
                            size="small"
                            fullWidth
                            label="UOM"
                            error={!!rowErrors[index]?.uom}
                            helperText={rowErrors[index]?.uom}
                          />
                        )}
                      />

                      {/* Stock Qty */}
                      <TextField
                        label="Stock Qty"
                        type="number"
                        fullWidth
                        size="small"
                        value={row.stock_qty || "0"}
                        disabled
                        InputProps={{
                          inputProps: { min: 0 },
                        }}
                      />

                      {/* Issue Qty */}
                      <TextField
                        label="Issue Qty"
                        type="number"
                        required
                        fullWidth
                        size="small"
                        value={row.issue_qty || "0"}
                        onChange={(e) => {
                          handleInputChange(index, "issue_qty", e.target.value);
                          const updated = [...rowErrors];
                          updated[index] = { ...updated[index], issue_qty: "" };
                          setRowErrors(updated);
                        }}
                        error={!!rowErrors[index]?.issue_qty}
                        helperText={rowErrors[index]?.issue_qty}
                        InputProps={{
                          inputProps: { min: 0 },
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            // ✅ Desktop View - Table
            <TableContainer>
              <Table
                size="small"
                sx={{ "& td, & th": { textAlign: "center", padding: "8px" } }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "#f5f5f5",
                      "& th": {
                        fontWeight: "bold",
                        color: "#333",
                        borderBottom: "1px solid #999",
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    <TableCell sx={{ minWidth: 80, maxWidth: 150 }}>
                      Sr. No.
                    </TableCell>
                    <TableCell>Category</TableCell>
                    {/* <TableCell>Sub Category</TableCell> */}
                    <TableCell>Item</TableCell>
                    <TableCell>Product Type</TableCell>
                    <TableCell>UOM</TableCell>
                    <TableCell>Stock Qty.</TableCell>
                    <TableCell>Issue Qty</TableCell>
                    <TableCell>Delete</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow
                      key={index}
                      hover={false}
                      sx={{
                        "&:hover": {
                          backgroundColor: "inherit !important",
                        },
                      }}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Autocomplete
                          options={categories}
                          getOptionLabel={(option) => option.name || ""}
                          value={row.category || null}
                          onChange={async (e, val, reason) => {
                            if (reason === "clear") {
                              handleInputChange(index, "category", null);
                              setSubcategories((prev) => {
                                const newSubcategories = [...prev];
                                newSubcategories[index] = [];
                                return newSubcategories;
                              });
                            }
                            handleInputChange(index, "category", val);
                          }}
                          onInputChange={(e, newValue, reason) => {
                            if (reason === "input") {
                              handleSearchInputChange(
                                index,
                                "category",
                                row,
                                newValue,
                              );
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Category"
                              size="small"
                              sx={{ width: "180px" }}
                              required
                              error={!!rowErrors[index]?.category}
                              helperText={rowErrors[index]?.category}
                            />
                          )}
                        />
                      </TableCell>
                      {/* <TableCell>
                        <Autocomplete
                          options={subcategories[index] || []}
                          getOptionLabel={(option) => option.name || ""}
                          value={row.sub_category || null}
                          onChange={async (e, val, reason) => {
                            if (reason === "clear") {
                              await handleSearchInputChange(
                                index,
                                "sub_category",
                                row,
                                "",
                              );
                              handleInputChange(index, "sub_category", null);
                            }
                            handleInputChange(index, "sub_category", val);
                          }}
                          onInputChange={(e, newValue, reason) => {
                            if (reason === "input") {
                              handleSearchInputChange(
                                index,
                                "sub_category",
                                row,
                                newValue,
                              );
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Subcategory"
                              size="small"
                              sx={{ width: "180px" }}
                              required
                              error={!!rowErrors[index]?.sub_category}
                              helperText={rowErrors[index]?.sub_category}
                            />
                          )}
                        />
                      </TableCell>*/}
                      <TableCell> 
                        <Autocomplete
                          options={itemOptions[index] || []}
                          getOptionLabel={(option) => option.name || ""}
                          isOptionEqualToValue={(option, value) =>
                            option?.id === value?.id
                          }
                          value={row.item_description || null}
                          onChange={async (e, newVal, reason) => {
                            if (reason === "clear") {
                              await handleSearchInputChange(
                                index,
                                "item",
                                row,
                                "",
                              );
                              handleInputChange(
                                index,
                                "item_description",
                                null,
                              );
                            }
                            handleInputChange(
                              index,
                              "item_description",
                              newVal,
                            );
                          }}
                          onInputChange={(e, newValue, reason) => {
                            if (reason === "input") {
                              handleSearchInputChange(
                                index,
                                "item",
                                row,
                                newValue,
                              );
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              required
                              label="Item"
                              variant="outlined"
                              size="small"
                              error={!!rowErrors[index]?.item_description}
                              helperText={rowErrors[index]?.item_description}
                              sx={{ width: "180px" }}
                            />
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <Autocomplete
                          options={size}
                          getOptionLabel={(opt) => opt?.size || opt?.name || ""}
                          isOptionEqualToValue={(option, value) =>
                            option?.id === value?.id
                          }
                          value={row.size || null}
                          onChange={(e, val, reason) => {
                            if (reason === "clear" || !val) {
                              handleInputChange(index, "size", null);
                              return;
                            }
                            handleInputChange(index, "size", val);
                          }}
                          onInputChange={(e, newValue, reason) => {
                            if (reason === "input") {
                              handleSearchInputChange(
                                index,
                                "size",
                                row,
                                newValue,
                              );
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Product Type"
                              size="small"
                              sx={{ width: "130px" }}
                            />
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <Autocomplete
                          options={unitOptions}
                          getOptionLabel={(option) =>
                            option.symbol || option.name || ""
                          }
                          isOptionEqualToValue={(option, value) =>
                            option?.id === value?.id
                          }
                          value={
                            unitOptions.find((opt) => opt.symbol === row.uom) ||
                            (row.uom
                              ? { symbol: row.uom, name: row.uom }
                              : null)
                          }
                          onChange={(e, newVal) => {
                            handleInputChange(
                              index,
                              "uom",
                              newVal?.symbol || newVal?.name || "",
                            );
                            const updated = [...rowErrors];
                            updated[index] = { ...updated[index], uom: "" };
                            setRowErrors(updated);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              required
                              size="small"
                              error={!!rowErrors[index]?.uom}
                              helperText={rowErrors[index]?.uom}
                              sx={{ width: "130px" }}
                            />
                          )}
                        />
                      </TableCell>

                      <TableCell>
                        <TextField
                          type="number"
                          required
                          value={row.stock_qty || "0"}
                          disabled
                          size="small"
                          sx={{
                            width: "120px",
                            "& .MuiInputBase-root": {
                              height: 40,
                              fontSize: "0.8rem",
                            },
                          }}
                          InputProps={{
                            inputProps: { min: 0 },
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <TextField
                          type="number"
                          required
                          value={row.issue_qty || "0"}
                          onChange={(e) => {
                            handleInputChange(
                              index,
                              "issue_qty",
                              e.target.value,
                            );
                            const updated = [...rowErrors];
                            updated[index] = {
                              ...updated[index],
                              issue_qty: "",
                            };
                            setRowErrors(updated);
                          }}
                          error={!!rowErrors[index]?.issue_qty}
                          helperText={rowErrors[index]?.issue_qty}
                          size="small"
                          sx={{
                            width: "120px",
                            "& .MuiInputBase-root": {
                              height: 40,
                              fontSize: "0.8rem",
                            },
                          }}
                          InputProps={{
                            inputProps: { min: 0 },
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <Tooltip title="Delete Row">
                          <IconButton
                            onClick={() => handleDeleteRow(index)}
                            color="error"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Dialog
            open={openItemModal}
            onClose={handleCloseItemModal}
            fullWidth
            maxWidth="md"
          >
            <DialogTitle>Add Item</DialogTitle>
            <DialogContent>
              <AddItemForm mode="modal" onClose={handleCloseItemModal} />
            </DialogContent>
          </Dialog>

          <Box mt={2} textAlign="center">
            <Tooltip title="Add Row">
              <IconButton onClick={handleAddRow} color="primary" size="large">
                <AddCircleOutlineIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default IssueForm;
